/**
 * Add/Edit modal for resident todos (spec 2026-08-11 §7; repeats: spec
 * 2026-10-02 §7.2, mock artboards 1–4, 6–7). Renders through the house
 * `UnifiedModal` with `variant="sheet"` — a bottom sheet on mobile, and, since
 * UnifiedModal downgrades sheet → dialog at the desktop breakpoint, a centered
 * dialog on desktop web (`dialogMaxWidth` keeps the 420px the hand-rolled card
 * used to have). UnifiedModal owns the screen-level layer (its own transparent
 * RN `Modal`, which is what lets the section mount this mid-page inside
 * HomeScreen's ScrollView), the scrim, swipe-to-dismiss, Android hardware
 * back, and keyboard avoidance, so this file is only the form.
 *
 * `scrollable` matters here: the sheet caps at 90% of the screen, and the iOS
 * date spinner, the Repeat panel and an open keyboard easily overflow that —
 * the body has to scroll rather than clip the notes field.
 *
 * Mode derives from `todo`: null → create, non-null → edit (prefilled). A
 * one-off deletes with a two-tap confirm. A Time row (Any time | At time, spec
 * 2026-10-06) sits under the date; its picker is one more `openPicker` value.
 * A REPEATING date (`todo.seriesId`) swaps the footer for the "where does this
 * apply?" step on Save (when anything changed) and on Delete — inside this
 * sheet, never a second modal: stacked native modals have frozen this app
 * before. Only one picker is open at a time (PickerField owns the platform
 * split, through DateField and TimeField).
 */
import React, { useEffect, useState } from 'react';
import { AccessibilityInfo, Platform, View, StyleSheet } from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemedStyles, useThemeColors } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import { onColor } from '@/constants/shared/theme';
import {
  addDaysToKey,
  firstDate,
  followDate,
  matchPreset,
} from '@/utils/residents/todoRecurrence';
import {
  deleteChoices,
  diffValues,
  directSaveScope,
  saveChoices,
  valuesFromTodo,
} from '@/utils/residents/todoScope';
import { toDateKey } from '@/utils/residents/todoCalendar';
import { defaultTime, toTimeKey } from '@/utils/residents/todoTime';
import type {
  EditScope,
  RepeatRule,
  ResidentTodo,
  TodoFormValues,
} from '@/types/residents/todos';
import { ChoiceChip } from './ChoiceChip';
import { DateField } from './DateField';
import { RepeatField } from './RepeatField';
import { ScopeChoices } from './ScopeChoices';
import { TimeField } from './TimeField';

export type { TodoFormValues } from '@/types/residents/todos';

/** Where "Ends: On date" starts out — far enough to cover a season of chores. */
const DEFAULT_ENDS_AFTER_DAYS = 90;

interface TodoFormModalProps {
  visible: boolean;
  /** null → create mode; a todo → edit mode (prefilled). */
  todo: ResidentTodo | null;
  /** Create-mode due date ('YYYY-MM-DD') — the calendar's active day. */
  initialDateKey: string;
  /** `scope` is passed only for a repeating date: the save step's choice. */
  onSave: (values: TodoFormValues, scope?: EditScope) => Promise<unknown>;
  /** `scope` is passed only for a repeating date: the delete step's choice. */
  onDelete: (todo: ResidentTodo, scope?: EditScope) => Promise<unknown>;
  onClose: () => void;
  /** The clock "At time" reads for its default (tests pin it). Default: the device clock at the tap. */
  now?: Date;
}

type Step = 'form' | 'save' | 'delete';
type OpenPicker = 'due' | 'ends' | 'time' | null;

/**
 * The date the repeat rule hangs off: what the presets, the summary and the Custom panel read their
 * weekday / day / month from. A repeating date keeps its SCHEDULED date for as long as the form is
 * open, even when the date field moves — the rule's parts came from it, and the field shows (and
 * edits) the day the item sits on, which a moved date makes a different one. A new todo, or a
 * one-off being converted, starts its repeat on the form's date itself.
 */
const ruleAnchor = (todo: ResidentTodo | null, dateKey: string): string =>
  todo?.seriesId ? (todo.occurrenceDate ?? todo.dueDate) : dateKey;

export const TodoFormModal: React.FC<TodoFormModalProps> = ({
  visible,
  todo,
  initialDateKey,
  onSave,
  onDelete,
  onClose,
  now,
}) => {
  const [initialValues, setInitialValues] = useState<TodoFormValues>(() =>
    valuesFromTodo(todo, initialDateKey),
  );
  const [title, setTitle] = useState('');
  const [dateKey, setDateKey] = useState(initialDateKey);
  const [notes, setNotes] = useState('');
  const [dueTime, setDueTime] = useState<string | null>(null);
  const [repeat, setRepeat] = useState<RepeatRule | null>(null);
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [customRepeat, setCustomRepeat] = useState(false);
  const [openPicker, setOpenPicker] = useState<OpenPicker>(null);
  const [step, setStep] = useState<Step>('form');
  const [busy, setBusy] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which subject the fields above were last seeded for. The first commit after the form reopens
  // still holds the PREVIOUS session's values against the new todo (the re-seed below runs in an
  // effect), so `seeded` is false for that commit and the announcements further down wait. Closing
  // clears the key, so reopening on the SAME todo and date doesn't look seeded either.
  const subjectKey = `${visible}|${todo?.id ?? ''}|${initialDateKey}`;
  const [seededKey, setSeededKey] = useState(subjectKey);
  const seeded = seededKey === subjectKey;

  // Re-seed the form whenever the modal opens or its SUBJECT changes.
  //
  // Keyed on `todo?.id`, deliberately not on `todo`: row identity churns
  // underneath us. residentTodosApi's optimistic `updateQueryData` patch
  // rewrites the edited row (Immer → new object), and `patchResult.undo()`
  // rewrites it again when the request fails. Depending on the object would
  // re-run this effect twice on a rejected save, reverting the user's typed
  // edits to server values and clearing the inline error the catch just set.
  // `todo?.id` still flips on create↔edit, on todo→different-todo (a
  // repeating date's id carries its date), and `visible` covers close→reopen.
  useEffect(() => {
    if (visible) {
      const seed = valuesFromTodo(todo, initialDateKey);
      setInitialValues(seed);
      setTitle(seed.title);
      setDateKey(seed.dueDate);
      setNotes(seed.notes ?? '');
      setDueTime(seed.dueTime);
      setRepeat(seed.repeat);
      setRepeatOpen(false);
      setCustomRepeat(
        seed.repeat ? matchPreset(seed.repeat, ruleAnchor(todo, seed.dueDate)) === 'custom' : false,
      );
      setOpenPicker(null);
      setStep('form');
      setBusy(false);
      setConfirmingDelete(false);
      setError(null);
      setSeededKey(subjectKey);
    } else {
      setSeededKey(''); // closed: nothing is seeded (see `subjectKey`)
    }
    // `todo` is intentionally excluded — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, todo?.id, initialDateKey]);

  const { colors: themeColors } = useThemeColors();
  const type = useTypography();
  const space = useSpacing();
  const radius = useRadius();
  const styles = useThemedStyles(({ colors, semantic }) =>
    StyleSheet.create({
      // Merged onto UnifiedModal's scroll content container, which already
      // supplies the horizontal padding; this only adds the field rhythm and
      // clears the header's bottom border.
      body: {
        paddingTop: space.lg,
        gap: space.lg,
      },
      footerContent: {
        gap: space.sm,
      },
      // `colors.text.muted` is 4.48:1 on the light sheet, under the 4.5:1 AA floor for this size.
      fieldLabel: {
        ...type.small,
        color: semantic.text.secondary,
      },
      input: {
        ...type.body,
        color: colors.text.primary,
        backgroundColor: semantic.background.input,
        borderColor: semantic.border.input,
        borderWidth: 1,
        borderRadius: radius.md,
        paddingHorizontal: space.md,
        paddingVertical: space.sm,
      },
      notesInput: {
        minHeight: 72,
        textAlignVertical: 'top',
      },
      chipRow: {
        flexDirection: 'row',
        gap: space.sm,
      },
      errorText: {
        ...type.small,
        color: semantic.status.errorText,
      },
      actionsRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: space.md,
      },
      cancelText: {
        ...type.body,
        color: colors.text.secondary,
      },
      // A padded box pulled back by the same amounts: the word stays put, the
      // hover tint gets room around it, and the padding is the 8pt reach the
      // hitSlop used to give.
      cancelButton: {
        paddingHorizontal: space.sm,
        paddingVertical: space.xs,
        marginHorizontal: -space.sm,
        marginVertical: -space.xs,
        borderRadius: radius.md,
      },
      saveButton: {
        backgroundColor: semantic.interactive.primary,
        borderRadius: radius.md,
        paddingHorizontal: space.lg,
        paddingVertical: space.sm,
      },
      saveButtonDisabled: {
        opacity: 0.5,
      },
      saveText: {
        // `type.button` (14/600) is the primary scale's button-label token —
        // the brief named `bodyMedium`, which lives only on MOBILE_TYPOGRAPHY,
        // not on what useTypography() returns. Matches the planner's other
        // button inks ("+ Add", Retry).
        ...type.button,
        color: onColor(semantic.interactive.primary),
      },
      deleteButton: {
        backgroundColor: semantic.interactive.danger,
        borderRadius: radius.md,
        paddingHorizontal: space.lg,
        paddingVertical: space.sm,
        marginRight: 'auto',
      },
      deleteText: {
        ...type.button,
        color: onColor(semantic.interactive.danger),
      },
    }),
  );

  const trimmedNotes = notes.trim();
  const values: TodoFormValues = {
    title: title.trim(),
    dueDate: dateKey,
    dueTime,
    notes: trimmedNotes.length > 0 ? trimmedNotes : undefined,
    repeat,
  };
  // A repeat with no dates at all can't be saved. While creating, or converting a one-off, the repeat
  // starts on the form's date, so it needs a first date from there (an end before the start, or
  // before the first matching day, leaves none). A repeating date is judged by its CHOICES instead:
  // a date moved past the Ends date is still a legal "Only this date" save, so the message shows
  // only when the change needs a scope and `saveChoices` has dropped every one.
  const noDates = todo?.seriesId
    ? diffValues(initialValues, values).any && saveChoices(todo, initialValues, values).length === 0
    : repeat !== null && firstDate(repeat, dateKey) === null;
  const repeatError = noDates ? 'Ends must be after the first date' : null;
  // iOS VoiceOver ignores `accessibilityLiveRegion` (Android / web only), so on iOS the two messages
  // that appear in the form are announced explicitly, once each, when they show up (each effect is
  // keyed on its message). Android and web keep the polite live regions. Neither speaks while the
  // form is not `seeded`: a message computed from the last session's values is not the form's.
  useEffect(() => {
    if (Platform.OS === 'ios' && seeded && error) AccessibilityInfo.announceForAccessibility(error);
  }, [error, seeded]);
  useEffect(() => {
    if (Platform.OS === 'ios' && seeded && repeatError) {
      AccessibilityInfo.announceForAccessibility(repeatError);
    }
  }, [repeatError, seeded]);
  // No `if (!visible) return null` guard: UnifiedModal has to stay mounted
  // through its own exit animation, and it already renders nothing once that
  // finishes.
  const canSave = title.trim().length > 0 && !busy && repeatError === null;
  // While creating, a repeat STARTS on this date.
  const dateLabel = !todo && repeat ? 'Starts' : 'Due date';

  const togglePicker = (picker: 'due' | 'ends' | 'time') => setOpenPicker(p => (p === picker ? null : picker));

  const handleDueDateChange = (key: string) => {
    // Creating, or converting a one-off: the repeat (re)starts here, so presets follow the date
    // (spec §5.3). A repeating date's rule stays put — it follows the date only at save time, and
    // only for the choices that restart it (todoScope).
    if (!todo?.seriesId && repeat) setRepeat(followDate(repeat, dateKey, key));
    setDateKey(key);
  };

  const handleRepeatChange = (rule: RepeatRule | null, custom: boolean) => {
    setRepeat(rule);
    setCustomRepeat(custom);
    if (rule === null && openPicker === 'ends') setOpenPicker(null);
  };

  const setEndsOn = (on: boolean) => {
    if (!repeat) return;
    if (!on) {
      setRepeat({ ...repeat, until: null });
      if (openPicker === 'ends') setOpenPicker(null);
      return;
    }
    if (repeat.until === null) {
      setRepeat({ ...repeat, until: addDaysToKey(dateKey, DEFAULT_ENDS_AFTER_DAYS) });
    }
    setOpenPicker('ends');
  };

  // At time: a todo with no time yet takes the default (spec §5.4: the next whole hour today, else
  // 9 AM); either way its picker opens, closing any other. Any time: the time goes, and its picker
  // closes with it.
  const setAtTime = (on: boolean) => {
    if (!on) {
      setDueTime(null);
      if (openPicker === 'time') setOpenPicker(null);
      return;
    }
    if (dueTime === null) {
      const clock = now ?? new Date();
      setDueTime(defaultTime(dateKey, toDateKey(clock), toTimeKey(clock)));
    }
    setOpenPicker('time');
  };

  const commitSave = async (scope?: EditScope) => {
    // The body stays editable behind the scope step, so the form can have gone stale since Save: the
    // title cleared, which the choices never look at. Send nothing and go back to the form, which
    // shows why Save is disabled. (An Ends error can't get here: the step only offers the choices
    // `saveChoices` kept, and the message shows exactly when none is left. `canSave` is the one rule,
    // so it is checked whole.) While busy a request is already out: leave the step alone.
    if (!canSave) {
      if (!busy) setStep('form');
      return;
    }
    // If every edit was undone there, nothing is left to apply, and a choice (a "This and following"
    // split, say) would go out for no change: close, exactly as saving an untouched repeating date
    // does (`handleSave`). A valid but unchanged form closes; an invalid one went back above.
    if (todo?.seriesId && !diffValues(initialValues, values).any) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await (scope ? onSave(values, scope) : onSave(values));
      onClose();
    } catch {
      setError("Couldn't save. Please try again.");
      setBusy(false);
      setStep('form');
    }
  };

  const handleSave = () => {
    if (!canSave) return;
    if (todo?.seriesId) {
      if (!diffValues(initialValues, values).any) {
        onClose();
        return;
      }
      // A change with one choice left has nothing to ask: it saves with that choice (todoScope).
      const direct = directSaveScope(todo, initialValues, values);
      if (direct) {
        void commitSave(direct);
        return;
      }
      setError(null);
      setStep('save');
      return;
    }
    void commitSave();
  };

  const commitDelete = async (scope?: EditScope) => {
    if (!todo) return;
    setBusy(true);
    setError(null);
    try {
      await (scope ? onDelete(todo, scope) : onDelete(todo));
      onClose();
    } catch {
      setError("Couldn't delete. Please try again.");
      setBusy(false);
      setConfirmingDelete(false);
      setStep('form');
    }
  };

  const handleDelete = () => {
    if (!todo || busy) return;
    if (todo.seriesId) {
      setError(null);
      setStep('delete');
      return;
    }
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    void commitDelete();
  };

  const formFooter = (
    // The error lives in the footer, not at the end of the body: the body
    // scrolls, and a save failure that scrolls out of view alongside the Save
    // button the user just pressed reads as nothing having happened. It is a
    // polite live region, so a screen reader announces that the save failed.
    <View style={styles.footerContent}>
      {error ? (
        <Text style={styles.errorText} accessibilityLiveRegion="polite">
          {error}
        </Text>
      ) : null}
      <View style={styles.actionsRow}>
        {todo ? (
          <Hoverable
            hover="button"
            style={styles.deleteButton}
            onPress={handleDelete}
            accessibilityRole="button"
            accessibilityLabel={confirmingDelete ? 'Confirm delete todo' : 'Delete todo'}
            accessibilityState={{ disabled: busy }}
          >
            <Text style={styles.deleteText}>{confirmingDelete ? 'Confirm delete' : 'Delete'}</Text>
          </Hoverable>
        ) : null}
        <Hoverable
          hover="row"
          style={styles.cancelButton}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Cancel todo form"
        >
          <Text style={styles.cancelText}>Cancel</Text>
        </Hoverable>
        <Hoverable
          hover="button"
          style={[styles.saveButton, !canSave && styles.saveButtonDisabled]}
          onPress={handleSave}
          disabled={!canSave}
          accessibilityRole="button"
          accessibilityLabel="Save todo"
          accessibilityState={{ disabled: !canSave }}
        >
          <Text style={styles.saveText}>Save</Text>
        </Hoverable>
      </View>
    </View>
  );

  const footer =
    step === 'save' && todo ? (
      <ScopeChoices
        heading="Apply changes to"
        choices={saveChoices(todo, initialValues, values)}
        busy={busy}
        onChoose={scope => void commitSave(scope)}
        onBack={() => setStep('form')}
      />
    ) : step === 'delete' && todo ? (
      <ScopeChoices
        heading="Delete repeating todo"
        destructive
        choices={deleteChoices(todo)}
        busy={busy}
        onChoose={scope => void commitDelete(scope)}
        onBack={() => setStep('form')}
      />
    ) : (
      formFooter
    );

  return (
    <UnifiedModal
      visible={visible}
      onClose={onClose}
      // Back / Escape on a scope step returns to the form; swipe and scrim still close (via onClose).
      onRequestClose={step === 'form' ? onClose : () => { if (!busy) setStep('form'); }}
      variant="sheet"
      title={todo ? 'Edit todo' : 'New todo'}
      dialogMaxWidth={420}
      avoidKeyboard
      scrollable
      contentContainerStyle={styles.body}
      footer={footer}
    >
      <Text style={styles.fieldLabel}>Title</Text>
      <TextInput
        style={styles.input}
        value={title}
        onChangeText={setTitle}
        placeholder="What needs doing?"
        placeholderTextColor={themeColors.text.dimmer}
        // Server column limits — cap here so a long paste can't round-trip
        // into a generic 422.
        maxLength={255}
        autoFocus={!todo}
        accessibilityLabel="Todo title"
      />

      <Text style={styles.fieldLabel}>{dateLabel}</Text>
      <DateField
        label={dateLabel}
        pickerName={dateLabel === 'Starts' ? 'start date' : 'due date'}
        value={dateKey}
        open={openPicker === 'due'}
        onToggle={() => togglePicker('due')}
        onClose={() => setOpenPicker(null)}
        onChange={handleDueDateChange}
      />

      <Text style={styles.fieldLabel}>Time</Text>
      <View style={styles.chipRow}>
        <ChoiceChip label="Any time" selected={dueTime === null} onPress={() => setAtTime(false)} />
        <ChoiceChip label="At time" selected={dueTime !== null} onPress={() => setAtTime(true)} />
      </View>
      {dueTime !== null ? (
        <TimeField
          value={dueTime}
          dateKey={dateKey}
          open={openPicker === 'time'}
          onToggle={() => togglePicker('time')}
          onClose={() => setOpenPicker(null)}
          onChange={setDueTime}
        />
      ) : null}

      <Text style={styles.fieldLabel}>Repeat</Text>
      <RepeatField
        rule={repeat}
        startKey={ruleAnchor(todo, dateKey)}
        open={repeatOpen}
        custom={customRepeat}
        onToggleOpen={() => setRepeatOpen(o => !o)}
        onChange={handleRepeatChange}
      />

      {repeat ? (
        <>
          <Text style={styles.fieldLabel}>Ends</Text>
          <View style={styles.chipRow}>
            <ChoiceChip
              label="Never"
              accessibilityLabel="Never ends"
              selected={repeat.until === null}
              onPress={() => setEndsOn(false)}
            />
            <ChoiceChip
              label="On date"
              accessibilityLabel="Ends on date"
              selected={repeat.until !== null}
              onPress={() => setEndsOn(true)}
            />
          </View>
          {repeat.until !== null ? (
            <DateField
              label="Ends on"
              pickerName="end date"
              withYear
              value={repeat.until}
              open={openPicker === 'ends'}
              onToggle={() => togglePicker('ends')}
              onClose={() => setOpenPicker(null)}
              onChange={key => setRepeat({ ...repeat, until: key })}
            />
          ) : null}
          {/* Polite live region: it is also why Save went disabled, so say so. */}
          {repeatError ? (
            <Text style={styles.errorText} accessibilityLiveRegion="polite">
              {repeatError}
            </Text>
          ) : null}
        </>
      ) : null}

      <Text style={styles.fieldLabel}>Notes (optional)</Text>
      <TextInput
        style={[styles.input, styles.notesInput]}
        value={notes}
        onChangeText={setNotes}
        maxLength={2000}
        multiline
        accessibilityLabel="Todo notes"
      />
    </UnifiedModal>
  );
};

TodoFormModal.displayName = 'TodoFormModal';
